import { LogicalOperator, ProjectCoreLang, Report } from '@aipb/gulliver/castor/model';
import { CatalogEnvService } from '@aipb/gulliver/catalog/env';
import { CATALOG_CASTOR_CONFIG_LOCAL_STORAGE_KEY } from '@aipb/gulliver/catalog/model';
import { AppConfigId } from '@aipb/gulliver/env/model';
import { APP_NAME } from '@aipb/gulliver/env/util';
import { selectLoading, updateLoading, withLoading } from '@aipb/shared/util/state';
import { inject, Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { excludeKeys, localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

interface CastorConfigProps {
  ui: {
    [configId in AppConfigId]: {
      activeCoreId: string;
      activeLogOperator: LogicalOperator;
    };
  };
  reports: Report[];
  cores: ProjectCoreLang[];
}

function initialProps(catalogEnv: CatalogEnvService): CastorConfigProps {
  const configIds = catalogEnv.getConfigIds();

  const ui = {};

  for (const configId of configIds) {
    ui[configId] = {
      activeCoreId: catalogEnv.getEnv(configId).castor.defaultCoreId,
      activeLogOperator: catalogEnv.getEnv(configId).castor.defaultLogOperator || 'AND'
    };
  }

  return {
    ui: ui,
    reports: [],
    cores: []
  };
}

@Injectable({
  providedIn: 'root'
})
export class CastorConfigRepository {
  catalogEnv = inject(CatalogEnvService);
  store = createStore(
    { name: 'catalog-castor-config' },
    withProps<CastorConfigProps>(initialProps(this.catalogEnv)),
    withLoading()
  );
  loading$ = this.store.pipe(selectLoading());
  cores$ = this.store.pipe(select((state) => state.cores));
  activeCoreId$: Observable<string> = this.store.pipe(select((state) => state.ui[this.configId].activeCoreId));
  activeLogicalOperator$ = this.store.pipe(select((state) => state.ui[this.configId].activeLogOperator));
  private appName = inject(APP_NAME);
  persist = persistState(this.store, {
    key: `${this.appName}-${CATALOG_CASTOR_CONFIG_LOCAL_STORAGE_KEY}`,
    storage: localStorageStrategy,
    source: () => this.store.pipe(excludeKeys(['loading', 'cores', 'reports']))
  });

  get activeLogOperator(): LogicalOperator {
    return this.store.getValue().ui[this.configId].activeLogOperator;
  }

  get configId() {
    return this.catalogEnv.activeConfigId;
  }

  set reports(reports: Report[]) {
    this.store.update((state) => ({
      ...state,
      reports
    }));
  }

  set cores(cores: ProjectCoreLang[]) {
    this.store.update((state) => ({
      ...state,
      cores
    }));
  }

  selectReports(coreId: string): Observable<Report[]> {
    return this.store.pipe(
      select((state) => state.reports),
      map((data) => {
        return data.filter((f) => f.coreId === coreId);
      })
    );
  }

  selectActiveCore(): Observable<ProjectCoreLang> {
    return combineLatest([this.cores$, this.activeCoreId$]).pipe(
      map((data) => {
        return data[0].find((f) => f.coreId === data[1]);
      })
    );
  }

  setActiveCoreId(coreId: string) {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          activeCoreId: coreId
        }
      }
    }));
  }

  setActiveLogicalOperator(logOp: LogicalOperator) {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          activeLogOperator: logOp
        }
      }
    }));
  }

  getActiveCoreId(): string {
    return this.store.getValue().ui[this.configId].activeCoreId;
  }

  getCores(): ProjectCoreLang[] {
    return this.store.getValue().cores;
  }

  setLoading(loading: boolean) {
    this.store.update(updateLoading(loading));
  }

  reset() {
    this.store.reset();
  }
}
